import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { signInWithCustomToken } from 'firebase/auth';
import styles from './Login.module.css';
import { 
    auth,
  loginWithGoogle, 
  loginWithKakao, 
  loginWithNaver, 
  mergeAnonymousData 
} from '../utils/firebase';
import { getUserId } from '../utils/userId';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';

  // 1. 카카오 리다이렉트 이후 "code" 처리 로직
  useEffect(() => {
    const processLogin = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state'); // 전송했던 hospitalId가 들어있음

      if (code) {
        try {
          const response = await axios.post("https://console.firebase.google.com/project/braincheck-d7af8/overview", {
            token: code 
          });

          const { firebaseToken } = response.data;

          // Firebase 커스텀 토큰으로 로그인
          const userCredential = await signInWithCustomToken(auth, firebaseToken);
          const user = userCredential.user;

          // 기존 데이터 병합
          const anonymousId = getUserId();
          if (anonymousId && user.uid !== anonymousId) {
            await mergeAnonymousData(user.uid, anonymousId);
          }

          // UID 갱신 및 원래 병원 페이지로 리다이렉트
          localStorage.setItem('userId', user.uid);
          const targetPrefix = (state === 'default' || !state) ? '' : `/${state}`;
          navigate(`${targetPrefix}/check`);

        } catch (error) {
          console.error("로그인 처리 중 에러:", error);
          alert(t('login_error', '로그인에 실패했습니다.'));
          navigate(`${prefix}/login`); // 에러 시 파라미터 없는 로그인창으로 정리
        }
      }
    };

    processLogin();
  }, [navigate, prefix, t]);

  const handleSocialLogin = async (provider: string) => {
    try {
      let user;
      const anonymousId = getUserId();

      if (provider === 'google') {
        user = await loginWithGoogle();
      } else if (provider === 'kakao') {
            const KAKAO_REST_API_KEY = process.env.REACT_APP_KAKAO_REST_API_KEY;
            const REDIRECT_URI = process.env.REACT_APP_KAKAO_REDIRECT_URI;
            const currentHospitalId = hospitalId || 'default';
            
            if (!KAKAO_REST_API_KEY || !REDIRECT_URI) {
            console.error("환경 변수가 설정되지 않았습니다.");
            return;
            }

            window.location.href = `https://kauth.kakao.com/oauth/authorize?response_type=code&client_id=${KAKAO_REST_API_KEY}&redirect_uri=${REDIRECT_URI}&state=${currentHospitalId}`;
            return;
        } else if (provider === 'naver') {
        user = await loginWithNaver();
      }

      if (user) {
        if (anonymousId) await mergeAnonymousData(user.uid, anonymousId);
        localStorage.setItem('userId', user.uid);
        navigate(`${prefix}/check`);
      }
    } catch (error) {
      console.error(error);
      alert(t('login_error', '로그인 중 오류가 발생했습니다.'));
    }
  };

  const KakaoSymbol = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
      <path fillRule="evenodd" clipRule="evenodd" d="M9 2.5C5.13401 2.5 2 4.96243 2 8C2 9.96541 3.28427 11.6877 5.15282 12.7237L4.3516 15.6385C4.30594 15.8045 4.49658 15.9327 4.63462 15.8412L8.06948 13.5647C8.37424 13.5878 8.68442 13.6 9 13.6C12.866 13.6 16 11.1376 16 8C16 4.86243 12.866 2.5 9 2.5Z" fill="black" />
    </svg>
  );

  const NaverSymbol = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
      <path d="M11.9033 9.32432L6.04618 1.00003H2V17.0001H6.09677V8.67576L11.9538 17.0001H16V1.00003H11.9033V9.32432Z" fill="white" />
    </svg>
  );

  const GoogleSymbol = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 48 48" 
    width="20" 
    height="20" 
    className={styles.googleSymbol} /* 전용 클래스 추가 */
  >
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6L40.1 6.22C35.83 2.37 30.29 0 24 0 14.65 0 6.79 5.41 3.12 13.29l7.65 5.94C12.55 14.12 17.79 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.91 24.5c0-1.61-.15-3.16-.41-4.66H24v9.09h12.87c-.55 2.98-2.24 5.51-4.78 7.21l7.42 5.75c4.34-4.01 6.82-9.92 6.82-16.39z"/>
    <path fill="#FBBC05" d="M10.77 28.77c-.47-1.41-.74-2.91-.74-4.47s.27-3.06.74-4.47l-7.65-5.94C1.12 17.22 0 20.48 0 24s1.12 6.78 3.12 10.11l7.65-5.34z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.42-5.75c-2.19 1.47-5 2.36-8.47 2.36-6.21 0-11.45-4.62-13.33-10.71l-7.65 5.94C6.79 42.59 14.65 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

  return (
    <div className={styles.container}>
      {/* 1. 상단 헤더 (백버튼 + 타이틀) */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t('login_title', '로그인')}</span>
      </div>

      {/* 2. 중앙 하얀색 카드 (핵심 레이아웃) */}
      <div className={styles.content}>
        <div className={styles.contentScroll}>
          <h2 className={styles.welcomeText}>
            {t('login_welcome', '안녕하세요!')}
          </h2>
          <p className={styles.description}>
            {t('login_desc', '간편 로그인으로 검사 기록을 연동하고\n나만의 건강 정보를 관리해보세요.')}
          </p>

          <div className={styles.buttonList}>
            <button className={`${styles.loginButton} ${styles.kakao}`} onClick={() => handleSocialLogin('kakao')}>
              <KakaoSymbol />
              <span className={styles.buttonText}>{t('login_kakao', '카카오 로그인')}</span>
            </button>
            <button className={`${styles.loginButton} ${styles.naver}`} onClick={() => handleSocialLogin('naver')}>
              <NaverSymbol />
              <span className={styles.buttonText}>{t('login_naver', '네이버 로그인')}</span>
            </button>
            <button className={`${styles.loginButton} ${styles.google}`} onClick={() => handleSocialLogin('google')}>
              <GoogleSymbol />
              <span className={styles.buttonText}>{t('login_google', 'Google 로그인')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;